import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/feedback';
import { Logo } from '@/components/ui/logo';
import { useEtatAuth } from './api/auth.api';
import { useSession } from './useSession';

function PleinEcran({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-surface-sunken px-4">
      <Logo ton="clair" />
      {children}
    </div>
  );
}

/**
 * Garde de route.
 *
 * Il ne suffit pas de vérifier qu'une session existe : encore faut-il que le
 * serveur en attende une. Derrière Cloudflare Access — ou en développement avec
 * `DEV_SKIP_AUTH` — il n'y a pas de session applicative, et rediriger vers
 * l'écran de connexion enfermerait l'utilisateur dans une boucle devant un
 * formulaire inutile. Le mode est donc demandé au serveur avant de trancher.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const etat = useEtatAuth();
  const session = useSession();
  const emplacement = useLocation();

  if (etat.isPending) {
    return (
      <PleinEcran>
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Spinner />
          Chargement…
        </p>
      </PleinEcran>
    );
  }

  /* Impossible de savoir ce que le serveur attend : ni ouvrir l'application,
   * ni prétendre que la session a expiré. On le dit. */
  if (etat.isError) {
    return (
      <PleinEcran>
        <p className="max-w-sm text-center text-sm text-ink-muted">
          Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.
        </p>
        <Button variant="primary" onClick={() => void etat.refetch()}>
          Réessayer
        </Button>
      </PleinEcran>
    );
  }

  if (etat.data.mode === 'session' && !session) {
    return (
      <Navigate
        to="/connexion"
        replace
        // Pour revenir exactement où l'on allait après la connexion.
        state={{ de: `${emplacement.pathname}${emplacement.search}` }}
      />
    );
  }

  return <>{children}</>;
}
